var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { CurrencyCode, PlanType, PlanTypeCategory } from '../../enum';
import { shouldDisableDate } from '../../util';
import { usePlatformContext } from '../app/selectors';
import { Autocomplete, DatePicker, FormField, NumberInput } from '../common';
import { ANNUAL_PERIOD_LENGTH, MAX_PERIOD_LENGTH, MONTHLY_PLAN_TYPES, getPlanTypeCategoryOptions, getPlanTypeOptions } from '../home/constants';
import { currencyAutocompleteListboxProps, useCurrencyOptions } from '../hooks';
import { TIME_INTERVAL } from '../wizard/constants';
import { DEFAULT_TIME_INTERVAL, selectOpenTimeOptions, useCloseTimeOptions } from '../wizard/workArrivalPattern/selectors';
import { useDownloadTemplateAPI } from './selectors';
export var DownloadTemplateDialog = function (_a) {
  var open = _a.open,
    onClose = _a.onClose;
  var t = useTranslation(['myPlans', 'translation', 'report', 'wizard']).t;
  var startTimeOptions = useMemo(function () {
    return selectOpenTimeOptions(DEFAULT_TIME_INTERVAL).slice(2);
  }, []);
  var defaultValues = useMemo(function () {
    return {
      numberOfQueues: 1,
      startDate: moment.utc().startOf('month'),
      planTypeCategory: PlanTypeCategory.WorkVolume,
      planningHorizon: PlanType.Monthly,
      periodLength: null,
      timeInterval: TIME_INTERVAL[0].id,
      currency: CurrencyCode.Dollar,
      isDaily: false,
      dailyStartTime: startTimeOptions[0].id,
      dailyEndTime: ''
    };
  }, [startTimeOptions]);
  var validateForm = yup.object().shape({
    numberOfQueues: yup.number().required(),
    startDate: yup.object().required(),
    startDateError: yup.string().optional().max(0, 'Invalid'),
    planTypeCategory: yup.string().required().trim(),
    planningHorizon: yup.string().required().trim(),
    periodLength: yup.number().required(),
    timeInterval: yup.number().required(),
    currency: yup.string().required(),
    dailyStartTime: yup.string().required(),
    dailyEndTime: yup.string().when('isDaily', {
      is: true,
      then: function (endTime) {
        return endTime.required();
      }
    })
  });
  var _b = useForm({
      defaultValues: defaultValues,
      resolver: yupResolver(validateForm)
    }),
    control = _b.control,
    watch = _b.watch,
    handleSubmit = _b.handleSubmit,
    setValue = _b.setValue,
    reset = _b.reset;
  var _c = watch(),
    planTypeCategory = _c.planTypeCategory,
    planningHorizon = _c.planningHorizon,
    timeInterval = _c.timeInterval,
    dailyStartTime = _c.dailyStartTime,
    isDaily = _c.isDaily;
  var _d = useDownloadTemplateAPI(planTypeCategory, open),
    exportTemplate = _d[0],
    fetchExportTemplate = _d[1],
    clearExportTemplate = _d[2];
  var endTimeOptions = useCloseTimeOptions(timeInterval, dailyStartTime);
  var context = usePlatformContext();
  var currencyOptions = useCurrencyOptions();
  useEffect(function () {
    if (open) {
      reset(defaultValues);
    }
  }, [defaultValues, open, reset]);
  useEffect(function () {
    var _a, _b;
    setValue('dailyEndTime', (_b = (_a = endTimeOptions[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '', {
      shouldValidate: true
    });
  }, [endTimeOptions, setValue, isDaily]);
  useEffect(function () {
    if (exportTemplate.data) {
      onClose();
    }
    return function () {
      clearExportTemplate();
    };
  }, [clearExportTemplate, exportTemplate.data, onClose]);
  // Auto-enable WAP for ProdHours plans (required)
  useEffect(function () {
    if (planTypeCategory === PlanTypeCategory.ProdHours && !isDaily) {
      setValue('isDaily', true, {
        shouldValidate: true
      });
    }
  }, [planTypeCategory, isDaily, setValue]);
  var isMonthlyPlanType = useMemo(function () {
    return MONTHLY_PLAN_TYPES.includes(planningHorizon);
  }, [planningHorizon]);
  var handleShouldDisableDate = useCallback(function (day) {
    return shouldDisableDate(planningHorizon, day);
  }, [planningHorizon]);
  var handleChangePlanningHorizon = function (newPlanType) {
    setValue('periodLength', null);
    setValue('startDate', MONTHLY_PLAN_TYPES.includes(newPlanType) ? moment.utc().startOf('month') : moment.utc());
  };
  var handleFormSubmit = function (planInfo) {
    var submitData = __assign(__assign({}, planInfo), {
      planType: planInfo.planningHorizon
    });
    if (planInfo.planningHorizon === PlanType.Weekly) return fetchExportTemplate(submitData);
    if (planInfo.periodLength && ANNUAL_PERIOD_LENGTH.has(planInfo.periodLength)) return fetchExportTemplate(submitData);
    return fetchExportTemplate(__assign(__assign({}, submitData), {
      planType: PlanType.MonthlyCustom
    }));
  };
  return _jsx(Dialog, __assign({
    open: open,
    onClose: onClose,
    maxWidth: "md"
  }, {
    children: _jsxs(Box, __assign({
      component: "form",
      onSubmit: handleSubmit(handleFormSubmit)
    }, {
      children: [_jsx(DialogTitle, __assign({
        variant: "h2"
      }, {
        children: t('download.createYourTemplate')
      })), _jsxs(DialogContent, {
        children: [_jsx(DialogContentText, __assign({
          sx: {
            mb: 3
          }
        }, {
          children: t('download.downloadTemplateSecondHeader')
        })), _jsxs(Grid, __assign({
          container: true,
          spacing: 4
        }, {
          children: [_jsx(Grid, __assign({
            item: true,
            xs: 6
          }, {
            children: _jsx(FormField, __assign({
              required: true,
              name: "numberOfQueues",
              label: t('download.numberOfQueues', {
                context: context
              }),
              control: control
            }, {
              children: _jsx(NumberInput, {
                min: 1,
                max: 999,
                fullWidth: true
              })
            }))
          })), _jsx(Grid, __assign({
            item: true,
            xs: 6
          }, {
            children: _jsx(FormField, __assign({
              required: true,
              name: "planTypeCategory",
              label: t('download.planTypeCategory'),
              control: control
            }, {
              children: _jsx(Autocomplete, {
                options: getPlanTypeCategoryOptions(),
                "data-testid": "planTypeCategory",
                getOptionValue: function (o) {
                  return o.value;
                }
              })
            }))
          })), _jsx(Grid, __assign({
            item: true,
            xs: 6
          }, {
            children: _jsx(FormField, __assign({
              required: true,
              name: "planningHorizon",
              label: t('download.planningHorizon'),
              control: control
            }, {
              children: _jsx(Autocomplete, {
                options: getPlanTypeOptions(),
                "data-testid": "planningHorizon",
                getOptionValue: function (o) {
                  return o.value;
                },
                onChange: handleChangePlanningHorizon
              })
            }))
          })), _jsx(Grid, __assign({
            item: true,
            xs: 6
          }, {
            children: _jsx(FormField, __assign({
              required: true,
              name: "startDate",
              label: t('planStartDate'),
              control: control
            }, {
              children: _jsx(DatePicker, {
                control: control,
                disableHighlightToday: isMonthlyPlanType,
                shouldDisableDate: handleShouldDisableDate
              })
            }))
          })), _jsx(Grid, __assign({
            item: true,
            xs: 6
          }, {
            children: _jsx(FormField, __assign({
              required: true,
              name: "periodLength",
              label: t('length'),
              control: control
            }, {
              children: _jsx(NumberInput, {
                "data-testid": "periodLength",
                placeholder: t("numberPlaceholder".concat(planningHorizon)),
                min: 1,
                max: MAX_PERIOD_LENGTH[planningHorizon],
                fullWidth: true
              })
            }))
          })), _jsx(Grid, __assign({
            item: true,
            xs: 6
          }, {
            children: _jsx(FormField, __assign({
              required: true,
              name: "timeInterval",
              label: t('download.timeInterVal'),
              control: control
            }, {
              children: _jsx(Autocomplete, {
                "data-testid": "workVolumeIntervals",
                options: TIME_INTERVAL
              })
            }))
          })), _jsx(Grid, __assign({
            item: true,
            xs: 6
          }, {
            children: _jsx(FormField, __assign({
              required: true,
              name: "currency",
              label: t('download.currency'),
              control: control
            }, {
              children: _jsx(Autocomplete, {
                options: currencyOptions,
                "data-testid": "currency",
                ListboxProps: currencyAutocompleteListboxProps
              })
            }))
          })), _jsx(Grid, __assign({
            item: true,
            xs: 12
          }, {
            children: _jsx(FormControlLabel, {
              control: _jsx(Checkbox, {
                size: "small",
                checked: isDaily,
                disabled: planTypeCategory === PlanTypeCategory.ProdHours,
                onChange: function (e, checked) {
                  return setValue('isDaily', checked, {
                    shouldValidate: true
                  });
                }
              }),
              label: planTypeCategory === PlanTypeCategory.ProdHours ? t('download.addWorkVolumeInterValsRequired') : t('download.addWorkVolumeInterVals')
            })
          })), isDaily && _jsxs(_Fragment, {
            children: [_jsx(Grid, __assign({
              item: true,
              xs: 6
            }, {
              children: _jsx(FormField, __assign({
                required: true,
                name: "dailyStartTime",
                label: t('download.earliestDailyEndTime'),
                control: control
              }, {
                children: _jsx(Autocomplete, {
                  options: startTimeOptions
                })
              }))
            })), _jsx(Grid, __assign({
              item: true,
              xs: 6
            }, {
              children: _jsx(FormField, __assign({
                required: true,
                name: "dailyEndTime",
                label: t('download.latestDailyEndTime'),
                control: control
              }, {
                children: _jsx(Autocomplete, {
                  options: endTimeOptions
                })
              }))
            }))]
          })]
        }))]
      }), _jsxs(DialogActions, __assign({
        sx: {
          justifyContent: 'space-between'
        }
      }, {
        children: [_jsx(Button, __assign({
          variant: "outlined",
          onClick: onClose
        }, {
          children: t('translation:action.cancel')
        })), _jsx(LoadingButton, __assign({
          variant: "contained",
          type: "submit",
          loading: exportTemplate.requesting
        }, {
          children: t('download.downloadTemplate')
        }))]
      }))]
    }))
  }));
};